import { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import {
  Plus, Play, Pause, Trash2, Save, Undo2, Redo2, ZoomIn, ZoomOut,
  Maximize2, Zap, GitBranch, Search, Sun, Globe, Send, Mail, Webhook,
  Database, UserPlus, Timer, StopCircle, MessageSquare, Clock, Bot, Users,
  FileText, ChevronRight, X, AlertTriangle, CheckCircle2, Eye,
  Settings, ArrowRight, Workflow, PanelLeftClose, PanelLeftOpen,
  Sparkles, Tags, RefreshCw, Copy, Scissors, Clipboard, Bug,
  Grid3x3, Map, Download,
} from 'lucide-react';
import {
  NODE_CATEGORIES, loadAutomations, saveAutomations,
  createAutomation, updateAutomation, deleteAutomation,
  validateFlow, simulateFlow,
} from '../data/automations';
import TemplateLibrary from './TemplateLibrary';

import { useTranslation } from 'react-i18next';
// ─── Icon map (string → component) ───
const ICON_MAP = {
  MessageSquare, Clock, Webhook, FileText, Bot, Search, Users, Sun, Globe,
  Send, Mail, Database, UserPlus, Timer, StopCircle, Sparkles, Tags, RefreshCw,
};

function GetIcon({ name, className = 'w-4 h-4' }) {
    const { t } = useTranslation();
const Icon = ICON_MAP[name] || Zap;
  return <Icon className={className} />;
}

// ─── Color helpers ───
const typeColorMap = {
  trigger: { border: 'border-amber-500/60', bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-500', ring: 'ring-amber-500/30', hover: 'hover:border-amber-500' },
  condition: { border: 'border-sky-500/60', bg: 'bg-sky-500/10', text: 'text-sky-400', dot: 'bg-sky-500', ring: 'ring-sky-500/30', hover: 'hover:border-sky-500' },
  action: { border: 'border-emerald-500/60', bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-500', ring: 'ring-emerald-500/30', hover: 'hover:border-emerald-500' },
  logic: { border: 'border-violet-500/60', bg: 'bg-violet-500/10', text: 'text-violet-400', dot: 'bg-violet-500', ring: 'ring-violet-500/30', hover: 'hover:border-violet-500' },
  ai: { border: 'border-fuchsia-500/60', bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-400', dot: 'bg-fuchsia-500', ring: 'ring-fuchsia-500/30', hover: 'hover:border-fuchsia-500' },
  end: { border: 'border-red-500/60', bg: 'bg-red-500/10', text: 'text-red-400', dot: 'bg-red-500', ring: 'ring-red-500/30', hover: 'hover:border-red-500' },
};

// ─── Toolbar ───
function Toolbar({ zoom, onZoomIn, onZoomOut, onFit, canUndo, canRedo, onUndo, onRedo, onSave, onTest, onValidate, isValid, validationErrors, isSaving, gridSnap, onToggleGridSnap, onOpenTemplates }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-950/80">
      <div className="flex items-center gap-2">
        <button onClick={onUndo} disabled={!canUndo} className="p-1.5 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title={t('automation_builder.undo_ctrlz')}>
          <Undo2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={onRedo} disabled={!canRedo} className="p-1.5 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title={t('automation_builder.redo_ctrlshiftz')}>
          <Redo2 className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        <button onClick={onZoomOut} className="p-1.5 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white transition-colors" title={t('automation_builder.zoom_out')}>
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <span className="font-mono text-[10px] text-zinc-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={onZoomIn} className="p-1.5 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white transition-colors" title={t('automation_builder.zoom_in')}>
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button onClick={onFit} className="p-1.5 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white transition-colors" title={t('automation_builder.fit_to_screen')}>
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        <button onClick={onToggleGridSnap} className={`p-1.5 border transition-colors ${gridSnap ? 'border-amber-500/50 bg-amber-500/10 text-amber-400' : 'border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white'}`} title={t('automation_builder.grid_snap')}>
          <Grid3x3 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-center gap-2">
        {onOpenTemplates && (
          <button onClick={onOpenTemplates} className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white font-mono text-[10px] uppercase tracking-wider transition-colors">
            <Download className="w-3.5 h-3.5" /> Templates
          </button>
        )}
        <button onClick={onValidate} className={`flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-wider transition-colors ${isValid ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white'}`}>
          {isValid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {isValid ? 'Valid' : 'Validate'}
        </button>
        {!isValid && validationErrors.length > 0 && (
          <div className="relative group">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <div className="absolute right-0 top-full mt-1 w-64 p-3 bg-zinc-900 border border-zinc-700 shadow-xl z-50 hidden group-hover:block">
              {validationErrors.map((err, i) => (
                <div key={i} className="font-mono text-[10px] text-amber-400 mb-1">• {err}</div>
              ))}
            </div>
          </div>
        )}
        <button onClick={onTest} className="flex items-center gap-1.5 px-3 py-1.5 border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 font-mono text-[10px] uppercase tracking-wider transition-colors">
          <Play className="w-3.5 h-3.5" /> Test
        </button>
        <button onClick={onSave} disabled={isSaving} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold uppercase tracking-wider text-[10px] transition-colors">
          <Save className="w-3.5 h-3.5" /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─── Node Panel (sidebar with draggable node types) ───
function NodePanel({ onAddNode, collapsed, onToggle }) {
  const { t } = useTranslation();
  const [expandedCategory, setExpandedCategory] = useState('trigger');

  if (collapsed) {
    return (
      <div className="w-12 border-r border-zinc-800 bg-zinc-950/50 flex flex-col items-center py-3">
        <button onClick={onToggle} className="p-2 text-zinc-500 hover:text-white transition-colors" title={t('automation_builder.expand_panel')}>
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-60 border-r border-zinc-800 bg-zinc-950/50 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('automation_builder.node_types')}</span>
        <button onClick={onToggle} className="p-1 text-zinc-500 hover:text-white transition-colors" title={t('automation_builder.collapse_panel')}>
          <PanelLeftClose className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {Object.entries(NODE_CATEGORIES).map(([key, cat]) => (
          <div key={key}>
            <button
              onClick={() => setExpandedCategory(expandedCategory === key ? null : key)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors ${expandedCategory === key ? 'bg-zinc-800/50' : 'hover:bg-zinc-800/30'}`}
            >
              <div className={`w-2 h-2 rounded-full ${cat.dot}`} />
              <span className={`font-mono text-[10px] uppercase tracking-wider ${cat.text}`}>{cat.label}</span>
              <span className="ml-auto font-mono text-[9px] text-zinc-600">{cat.items.length}</span>
            </button>
            {expandedCategory === key && (
              <div className="ml-3 mt-1 space-y-0.5">
                {cat.items.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => onAddNode(item.type, item.subtype, item.label)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 border ${cat.border} ${cat.bg} ${cat.text} hover:brightness-125 transition-all text-left`}
                  >
                    <GetIcon name={item.icon} className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-mono text-[9px] uppercase tracking-wider truncate">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Config Panel (node configuration) ───
function ConfigPanel({ node, onUpdate, onClose }) {
  const { t } = useTranslation();
  if (!node) {
    return (
      <div className="w-64 border-l border-zinc-800 bg-zinc-950/50 flex flex-col items-center justify-center p-4">
        <Settings className="w-8 h-8 text-zinc-700 mb-3" />
        <p className="font-mono text-[10px] text-zinc-600 text-center uppercase tracking-wider">{t('automation_builder.select_a_node_to_configure')}</p>
      </div>
    );
  }

  const colors = typeColorMap[node.type] || typeColorMap.action;

  const updateConfig = (key, value) => {
    onUpdate(node.id, { ...node.config, [key]: value });
  };

  return (
    <div className="w-64 border-l border-zinc-800 bg-zinc-950/50 flex flex-col overflow-hidden">
      <div className={`flex items-center justify-between px-3 py-2 border-b ${colors.border}`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
          <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${colors.text}`}>{node.type}</span>
        </div>
        <button onClick={onClose} className="p-1 text-zinc-500 hover:text-white transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Label */}
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.label')}</label>
          <input
            type="text"
            value={node.label}
            onChange={e => onUpdate(node.id, { label: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors"
          />
        </div>

        {/* Type-specific config fields */}
        {node.type === 'trigger' && (
          <>
            {node.subtype === 'new_message' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.channel')}</label>
                <select value={node.config.channel || 'any'} onChange={e => updateConfig('channel', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors">
                  <option value="any">{t('automation_builder.any_channel')}</option>
                  <option value="whatsapp">{t('referral.whatsapp')}</option>
                  <option value="telegram">{t('referral.telegram')}</option>
                  <option value="email">{t('referral.email')}</option>
                  <option value="discord">{t('referral.discord')}</option>
                </select>
              </div>
            )}
            {node.subtype === 'scheduled' && (
              <>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.cron_expression')}</label>
                  <input type="text" value={node.config.cron || ''} onChange={e => updateConfig('cron', e.target.value)} placeholder="0 9 * * *" className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.timezone')}</label>
                  <input type="text" value={node.config.timezone || ''} onChange={e => updateConfig('timezone', e.target.value)} placeholder={t('automation_builder.americanew_york')} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
                </div>
              </>
            )}
            {node.subtype === 'webhook' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.endpoint')}</label>
                <input type="text" value={node.config.endpoint || ''} onChange={e => updateConfig('endpoint', e.target.value)} placeholder="/api/webhooks/..." className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
              </div>
            )}
            {node.subtype === 'form_submit' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.form_id')}</label>
                <input type="text" value={node.config.form || ''} onChange={e => updateConfig('form', e.target.value)} placeholder="signup" className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
              </div>
            )}
          </>
        )}

        {node.type === 'condition' && (
          <>
            {node.subtype === 'keyword' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">Keywords (comma-separated)</label>
                <input type="text" value={(node.config.keywords || []).join(', ')} onChange={e => updateConfig('keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="pricing, cost, help" className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
              </div>
            )}
            {node.subtype === 'user_type' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.check')}</label>
                <select value={node.config.check || 'is_new'} onChange={e => updateConfig('check', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors">
                  <option value="is_new">{t('api_docs.automations.builder.is_new')}</option>
                  <option value="is_returning">{t('api_docs.automations.builder.is_returning')}</option>
                </select>
              </div>
            )}
            {node.subtype === 'business_hours' && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.start_hour')}</label>
                    <input type="number" min={0} max={23} value={node.config.startHour || 9} onChange={e => updateConfig('startHour', parseInt(e.target.value))} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.end_hour')}</label>
                    <input type="number" min={0} max={23} value={node.config.endHour || 18} onChange={e => updateConfig('endHour', parseInt(e.target.value))} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.timezone')}</label>
                  <input type="text" value={node.config.timezone || ''} onChange={e => updateConfig('timezone', e.target.value)} placeholder={t('automation_builder.americanew_york')} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
                </div>
              </>
            )}
            {node.subtype === 'channel' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.channel')}</label>
                <select value={node.config.channel || 'whatsapp'} onChange={e => updateConfig('channel', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors">
                  <option value="whatsapp">{t('referral.whatsapp')}</option>
                  <option value="telegram">{t('referral.telegram')}</option>
                  <option value="email">{t('referral.email')}</option>
                  <option value="discord">{t('referral.discord')}</option>
                </select>
              </div>
            )}
          </>
        )}

        {node.type === 'action' && (
          <>
            {(node.subtype === 'send_message' || node.subtype === 'send_email') && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">
                  {node.subtype === 'send_email' ? 'Template / Subject' : 'Message'}
                </label>
                <textarea
                  value={node.config.message || node.config.template || ''}
                  onChange={e => node.subtype === 'send_email' ? updateConfig('template', e.target.value) : updateConfig('message', e.target.value)}
                  placeholder={node.subtype === 'send_email' ? 'welcome' : 'Enter message...'}
                  rows={3}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors resize-none"
                />
              </div>
            )}
            {node.subtype === 'send_email' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">To</label>
                <input type="text" value={node.config.to || ''} onChange={e => updateConfig('to', e.target.value)} placeholder="team@maxemployee.com" className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
              </div>
            )}
            {node.subtype === 'call_webhook' && (
              <>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('api_docs.automations.builder.url')}</label>
                  <input type="text" value={node.config.url || ''} onChange={e => updateConfig('url', e.target.value)} placeholder="/api/..." className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.method')}</label>
                  <select value={node.config.method || 'POST'} onChange={e => updateConfig('method', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors">
                    <option value={t('automation_builder.get')}>GET</option>
                    <option value="POST">{t('api_docs.automations.builder.post')}</option>
                    <option value={t('automation_builder.put')}>PUT</option>
                  </select>
                </div>
              </>
            )}
            {node.subtype === 'update_crm' && (
              <>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.object')}</label>
                  <select value={node.config.object || 'lead'} onChange={e => updateConfig('object', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors">
                    <option value="lead">{t('api_docs.automations.builder.lead')}</option>
                    <option value="contact">{t('api_docs.automations.builder.contact')}</option>
                    <option value="deal">{t('api_docs.automations.builder.deal')}</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.action')}</label>
                  <select value={node.config.action || 'upsert'} onChange={e => updateConfig('action', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors">
                    <option value="upsert">{t('api_docs.automations.builder.upsert')}</option>
                    <option value="create">{t('common.create')}</option>
                    <option value="update">{t('common.update')}</option>
                  </select>
                </div>
              </>
            )}
            {node.subtype === 'assign_human' && (
              <>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.team')}</label>
                  <input type="text" value={node.config.team || ''} onChange={e => updateConfig('team', e.target.value)} placeholder="on-call" className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.priority')}</label>
                  <select value={node.config.priority || 'normal'} onChange={e => updateConfig('priority', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors">
                    <option value="low">{t('common.low')}</option>
                    <option value="normal">{t('common.normal')}</option>
                    <option value="high">{t('common.high')}</option>
                    <option value="urgent">{t('common.urgent')}</option>
                  </select>
                </div>
              </>
            )}
            {node.subtype === 'wait' && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">{t('automation_builder.duration')}</label>
                <input type="text" value={node.config.duration || ''} onChange={e => updateConfig('duration', e.target.value)} placeholder="24h, 30m, 5s" className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none transition-colors" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── SVG Connection Line ───
function ConnectionLine({ from, to, label, onDelete, zoom }) {
  if (!from || !to) return null;

  const x1 = from.x + 120 + 20; // right edge of source node
  const y1 = from.y + 30; // middle of source node
  const x2 = to.x - 20; // left edge of target node
  const y2 = to.y + 30; // middle of target node

  const midX = (x1 + x2) / 2;
  const cp1x = midX;
  const cp1y = y1;
  const cp2x = midX;
  const cp2y = y2;

  const pathD = `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;

  return (
    <g className="connection-group">
      {/* Invisible wider path for easier clicking */}
      <path d={pathD} fill="none" stroke="transparent" strokeWidth={12} className="cursor-pointer" onClick={onDelete} />
      {/* Visible path */}
      <path d={pathD} fill="none" stroke="rgb(251 191 36 / 0.5)" strokeWidth={2} className="pointer-events-none" />
      {/* Arrow marker at end */}
      <circle cx={x2} cy={y2} r={4} fill="rgb(251 191 36 / 0.7)" className="pointer-events-none" />
      {/* Label */}
      {label && (
        <text x={midX} y={(y1 + y2) / 2 - 6} textAnchor="middle" className="font-mono fill-zinc-500" style={{ fontSize: `${10 / zoom}px` }}>
          {label}
        </text>
      )}
    </g>
  );
}

// ─── Mini Map ───
function MiniMap({ nodes, pan, zoom, canvasWidth, canvasHeight }) {
  const { t } = useTranslation();
  if (nodes.length === 0) return null;

  const padding = 40;
  const minX = Math.min(...nodes.map(n => n.x)) - padding;
  const minY = Math.min(...nodes.map(n => n.y)) - padding;
  const maxX = Math.max(...nodes.map(n => n.x + 120)) + padding;
  const maxY = Math.max(...nodes.map(n => n.y + 60)) + padding;

  const worldW = maxX - minX;
  const worldH = maxY - minY;
  const mapW = 140;
  const mapH = 90;
  const scale = Math.min(mapW / worldW, mapH / worldH, 1);

  const vpX = (-pan.x / zoom - minX) * scale;
  const vpY = (-pan.y / zoom - minY) * scale;
  const vpW = (canvasWidth / zoom) * scale;
  const vpH = (canvasHeight / zoom) * scale;

  return (
    <div className="absolute bottom-3 right-3 border border-zinc-800 bg-zinc-950/90 z-30">
      <div className="px-2 py-1 border-b border-zinc-800 flex items-center gap-1">
        <Map className="w-3 h-3 text-zinc-500" />
        <span className="font-mono text-[8px] uppercase tracking-wider text-zinc-500">{t('automation_builder.map')}</span>
      </div>
      <svg width={mapW} height={mapH} className="block">
        {nodes.map(node => {
          const colors = typeColorMap[node.type] || typeColorMap.action;
          return (
            <rect
              key={node.id}
              x={(node.x - minX) * scale}
              y={(node.y - minY) * scale}
              width={120 * scale}
              height={40 * scale}
              fill={colors.dot.replace('bg-', 'rgb(').replace('-500', '') ? 'currentColor' : 'currentColor'}
              opacity={0.4}
              stroke={colors.dot.replace('bg-', '').replace('-500', '')}
              strokeWidth={0.5}
              rx={2}
            />
          );
        })}
        <rect
          x={Math.max(0, vpX)}
          y={Math.max(0, vpY)}
          width={Math.min(vpW, mapW)}
          height={Math.min(vpH, mapH)}
          fill="none"
          stroke="rgb(251 191 36 / 0.5)"
          strokeWidth={1}
          rx={1}
        />
      </svg>
    </div>
  );
}

// ─── Flow Canvas ───
function FlowCanvas({ nodes, connections, selectedNodeIds, connectingFrom, zoom, pan, gridSnap, onSelectNode, onMoveNode, onStartConnection, onEndConnection, onDeleteConnection, onCanvasClick, onPan, onMultiSelect, copiedNodes, onCopy, onPaste, canvasRef }) {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const GRID_SIZE = 20;

  const snapToGrid = (val) => {
    if (!gridSnap) return val;
    return Math.round(val / GRID_SIZE) * GRID_SIZE;
  };

  const handleMouseDown = (e, nodeId) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left - pan.x) / zoom;
    const mouseY = (e.clientY - rect.top - pan.y) / zoom;

    // Multi-select with Shift+Click
    if (e.shiftKey) {
      onMultiSelect(nodeId);
    } else {
      onSelectNode(nodeId);
    }

    setDragging(nodeId);
    setDragOffset({ x: mouseX - node.x, y: mouseY - node.y });
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left - pan.x) / zoom;
      const mouseY = (e.clientY - rect.top - pan.y) / zoom;
      const newX = snapToGrid(mouseX - dragOffset.x);
      const newY = snapToGrid(mouseY - dragOffset.y);
      onMoveNode(dragging, Math.max(0, newX), Math.max(0, newY));
    } else if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      onPan(pan.x + dx, pan.y + dy);
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setDragging(null);
    setIsPanning(false);
  };

  const handleCanvasMouseDown = (e) => {
    if (e.target === e.currentTarget || e.target.tagName === 'svg' || e.target.classList.contains('canvas-grid')) {
      if (e.button === 0) {
        onSelectNode(null);
        setIsPanning(true);
        setPanStart({ x: e.clientX, y: e.clientY });
      }
    }
  };

  const handleCopy = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      const activeTag = document.activeElement?.tagName;
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') return;
      if (selectedNodeIds.length > 0) {
        e.preventDefault();
        const nodesToCopy = nodes.filter(n => selectedNodeIds.includes(n.id));
        onCopy(nodesToCopy);
      }
    }
  };

  const handlePaste = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      const activeTag = document.activeElement?.tagName;
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') return;
      if (copiedNodes && copiedNodes.length > 0) {
        e.preventDefault();
        onPaste();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleCopy);
    window.addEventListener('keydown', handlePaste);
    return () => {
      window.removeEventListener('keydown', handleCopy);
      window.removeEventListener('keydown', handlePaste);
    };
  }, [selectedNodeIds, copiedNodes]);

  const handleDrop = (e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/node');
    if (data) {
      const nodeData = JSON.parse(data);
      const rect = canvasRef.current.getBoundingClientRect();
      const x = snapToGrid((e.clientX - rect.left - pan.x) / zoom - 60);
      const y = snapToGrid((e.clientY - rect.top - pan.y) / zoom - 20);
      onCanvasClick(nodeData, Math.max(0, x), Math.max(0, y));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  return (
    <div
      ref={canvasRef}
      className="flex-1 relative overflow-hidden cursor-grab active:cursor-grabbing"
      onMouseDown={handleCanvasMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* Grid background */}
      <div className="canvas-grid absolute inset-0" style={{
        backgroundImage: `
          linear-gradient(to right, rgb(255 255 255 / 0.03) 1px, transparent 1px),
          linear-gradient(to bottom, rgb(255 255 255 / 0.03) 1px, transparent 1px)
        `,
        backgroundSize: `${GRID_SIZE * zoom}px ${GRID_SIZE * zoom}px`,
        backgroundPosition: `${pan.x}px ${pan.y}px`,
      }} />

      {/* SVG layer for connections */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
        <g style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
          {connections.map((conn, i) => {
            const fromNode = nodes.find(n => n.id === conn.from);
            const toNode = nodes.find(n => n.id === conn.to);
            return (
              <ConnectionLine
                key={i}
                from={fromNode}
                to={toNode}
                label={conn.label}
                zoom={zoom}
                onDelete={() => onDeleteConnection(conn.from, conn.to)}
              />
            );
          })}
          {/* Connecting line preview */}
          {connectingFrom && (
            <line
              x1={connectingFrom.x + 140}
              y1={connectingFrom.y + 30}
              x2={connectingFrom.x + 200}
              y2={connectingFrom.y + 30}
              stroke="rgb(251 191 36 / 0.4)"
              strokeWidth={2}
              strokeDasharray="4 4"
            />
          )}
        </g>
      </svg>

      {/* Nodes layer */}
      <div className="absolute inset-0" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
        {nodes.map(node => {
          const colors = typeColorMap[node.type] || typeColorMap.action;
          const isSelected = selectedNodeIds.includes(node.id);
          const isConnecting = connectingFrom?.id === node.id;
          const cat = Object.values(NODE_CATEGORIES).find(c => c.items.some(i => i.subtype === node.subtype));
          const itemDef = cat?.items.find(i => i.subtype === node.subtype);
          const iconName = itemDef?.icon || 'Zap';

          return (
            <div
              key={node.id}
              className={`absolute select-none transition-shadow duration-200 ${isSelected ? `ring-2 ${colors.ring} shadow-lg` : ''} ${isConnecting ? 'ring-2 ring-amber-500/50' : ''}`}
              style={{ left: node.x, top: node.y, width: 120 }}
              onMouseDown={e => handleMouseDown(e, node.id)}
            >
              <div className={`border ${colors.border} ${colors.bg} p-2.5 cursor-move ${colors.hover} transition-colors`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <GetIcon name={iconName} className={`w-3.5 h-3.5 ${colors.text} shrink-0`} />
                  <span className={`font-mono text-[9px] uppercase tracking-wider ${colors.text} truncate`}>{node.label}</span>
                </div>
                <div className="font-mono text-[8px] text-zinc-600 uppercase">{node.type}</div>
                {node.breakpoint && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-500 flex items-center justify-center">
                    <Bug className="w-2 h-2 text-black" />
                  </div>
                )}
              </div>
              {/* Connection ports */}
              <div
                className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-zinc-700 border-2 border-zinc-500 hover:bg-amber-500 hover:border-amber-400 cursor-crosshair transition-colors"
                onMouseDown={e => { e.stopPropagation(); onStartConnection(node.id); }}
                title={t('automation_builder.drag_to_connect')}
              />
              <div
                className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-zinc-700 border-2 border-zinc-500 hover:bg-amber-500 hover:border-amber-400 cursor-crosshair transition-colors"
                onMouseUp={e => { e.stopPropagation(); onEndConnection(node.id); }}
                title={t('automation_builder.drop_connection_here')}
              />
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <Workflow className="w-12 h-12 text-zinc-800 mx-auto mb-3" />
            <p className="font-mono text-xs text-zinc-600 uppercase tracking-wider">{t('automation_builder.drag_nodes_from_the_panel_to_start_building')}</p>
            <p className="font-mono text-[10px] text-zinc-700 mt-1">{t('automation_builder.or_import')}</p>
          </div>
        </div>
      )}

      {/* Mini-map */}
      <MiniMap
        nodes={nodes}
        pan={pan}
        zoom={zoom}
        canvasWidth={canvasRef.current?.clientWidth || 800}
        canvasHeight={canvasRef.current?.clientHeight || 600}
      />
    </div>
  );
}

// ─── Simulation Panel ───
function SimulationPanel({ steps, onClose }) {
  if (!steps || steps.length === 0) return null;

  const typeColors = {
    start: 'text-amber-400',
    trigger: 'text-amber-400',
    condition: 'text-sky-400',
    action: 'text-emerald-400',
    logic: 'text-violet-400',
    ai: 'text-fuchsia-400',
    end: 'text-red-400',
    branch: 'text-zinc-500',
    complete: 'text-emerald-400',
    error: 'text-red-400',
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 max-h-64 bg-zinc-950 border-t border-zinc-800 z-40 flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-emerald-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t('automation_builder.flow_simulation')}</span>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {steps.map((step, i) => (
          <div key={i} className="flex items-start gap-2 font-mono text-[10px]">
            <span className="text-zinc-600 shrink-0 w-6 text-right">{i + 1}.</span>
            <span className={`uppercase tracking-wider shrink-0 w-20 ${typeColors[step.type] || 'text-zinc-400'}`}>[{step.type}]</span>
            <span className="text-zinc-300">{step.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Saved Automations List ───
function SavedAutomationsList({ automations, onLoad, onDelete, onNew }) {
  return (
    <div className="border-b border-zinc-800">
      <div className="flex items-center justify-between px-4 py-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('automation_builder.saved_automations')}</span>
        <button onClick={onNew} className="flex items-center gap-1 text-amber-500 hover:text-amber-400 font-mono text-[10px] uppercase tracking-wider transition-colors">
          <Plus className="w-3 h-3" /> New
        </button>
      </div>
      <div className="max-h-40 overflow-y-auto">
        {automations.map(auto => (
          <div key={auto.id} className="flex items-center justify-between px-4 py-2 hover:bg-zinc-800/30 transition-colors group">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${auto.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              <span className="font-mono text-xs text-zinc-300 truncate">{auto.name}</span>
              <span className="font-mono text-[9px] text-zinc-600">{auto.nodes?.length || 0}n</span>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => onLoad(auto)} className="p-1 text-zinc-500 hover:text-amber-400 transition-colors" title={t('automation_builder.load')}>
                <Eye className="w-3 h-3" />
              </button>
              <button onClick={() => onDelete(auto.id)} className="p-1 text-zinc-500 hover:text-red-400 transition-colors" title={t('automation_builder.delete')}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main AutomationBuilder Component ───
export default memo(function AutomationBuilder() {
  const [automations, setAutomations] = useState(() => loadAutomations());
  const [currentAutomation, setCurrentAutomation] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [connectingFrom, setConnectingFrom] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [nodePanelCollapsed, setNodePanelCollapsed] = useState(false);
  const [simulationSteps, setSimulationSteps] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationResult, setValidationResult] = useState({ valid: true, errors: [] });
  const [gridSnap, setGridSnap] = useState(true);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [copiedNodes, setCopiedNodes] = useState(null);

  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const canvasRef = useRef(null);
  const nodeIdCounter = useRef(1);

  // Push state to history
  const pushHistory = useCallback((newNodes, newConnections) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push({ nodes: JSON.parse(JSON.stringify(newNodes)), connections: JSON.parse(JSON.stringify(newConnections)) });
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      setNodes(JSON.parse(JSON.stringify(prev.nodes)));
      setConnections(JSON.parse(JSON.stringify(prev.connections)));
      setHistoryIndex(historyIndex - 1);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      setNodes(JSON.parse(JSON.stringify(next.nodes)));
      setConnections(JSON.parse(JSON.stringify(next.connections)));
      setHistoryIndex(historyIndex + 1);
    }
  }, [history, historyIndex]);

  // Add node
  const handleAddNode = useCallback((type, subtype, label, x, y) => {
    const id = `node-${nodeIdCounter.current++}`;
    const newNode = {
      id,
      type,
      subtype,
      label,
      x: x !== undefined ? x : 100 + Math.random() * 200,
      y: y !== undefined ? y : 100 + Math.random() * 200,
      config: {},
    };
    const newNodes = [...nodes, newNode];
    setNodes(newNodes);
    pushHistory(newNodes, connections);
    setSelectedNodeIds([id]);
  }, [nodes, connections, pushHistory]);

  // Move node
  const handleMoveNode = useCallback((nodeId, x, y) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, x: Math.max(0, x), y: Math.max(0, y) } : n));
  }, []);

  // Update node
  const handleUpdateNode = useCallback((nodeId, updates) => {
    setNodes(prev => {
      const updated = prev.map(n => n.id === nodeId ? { ...n, ...updates } : n);
      pushHistory(updated, connections);
      return updated;
    });
  }, [connections, pushHistory]);

  // Select node (single)
  const handleSelectNode = useCallback((nodeId) => {
    setSelectedNodeIds(nodeId ? [nodeId] : []);
  }, []);

  // Multi-select (Shift+Click)
  const handleMultiSelect = useCallback((nodeId) => {
    setSelectedNodeIds(prev => {
      if (prev.includes(nodeId)) {
        return prev.filter(id => id !== nodeId);
      }
      return [...prev, nodeId];
    });
  }, []);

  // Delete selected nodes
  const handleDeleteNode = useCallback(() => {
    if (selectedNodeIds.length === 0) return;
    const toDelete = new Set(selectedNodeIds);
    const newNodes = nodes.filter(n => !toDelete.has(n.id));
    const newConnections = connections.filter(c => !toDelete.has(c.from) && !toDelete.has(c.to));
    setNodes(newNodes);
    setConnections(newConnections);
    setSelectedNodeIds([]);
    pushHistory(newNodes, newConnections);
  }, [selectedNodeIds, nodes, connections, pushHistory]);

  // Copy nodes
  const handleCopy = useCallback((nodesToCopy) => {
    setCopiedNodes(JSON.parse(JSON.stringify(nodesToCopy)));
  }, []);

  // Paste nodes
  const handlePaste = useCallback(() => {
    if (!copiedNodes || copiedNodes.length === 0) return;
    const idMap = {};
    const newNodes = copiedNodes.map(node => {
      const newId = `node-${nodeIdCounter.current++}`;
      idMap[node.id] = newId;
      return { ...node, id: newId, x: node.x + 30, y: node.y + 30, config: { ...node.config } };
    });
    const newConnections = connections.filter(c => {
      const fromCopied = copiedNodes.some(n => n.id === c.from);
      const toCopied = copiedNodes.some(n => n.id === c.to);
      return !(fromCopied && toCopied);
    });
    // Add internal connections between pasted nodes
    const internalConnections = connections.filter(c => {
      const fromCopied = copiedNodes.some(n => n.id === c.from);
      const toCopied = copiedNodes.some(n => n.id === c.to);
      return fromCopied && toCopied;
    }).map(c => ({
      from: idMap[c.from] || c.from,
      to: idMap[c.to] || c.to,
      label: c.label,
    }));

    const allNodes = [...nodes, ...newNodes];
    const allConnections = [...newConnections, ...internalConnections];
    setNodes(allNodes);
    setConnections(allConnections);
    setSelectedNodeIds(newNodes.map(n => n.id));
    pushHistory(allNodes, allConnections);
  }, [copiedNodes, nodes, connections, pushHistory]);

  // Connection handling
  const handleStartConnection = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    setConnectingFrom(node);
  }, [nodes]);

  const handleEndConnection = useCallback((nodeId) => {
    if (connectingFrom && connectingFrom.id !== nodeId) {
      const exists = connections.some(c => c.from === connectingFrom.id && c.to === nodeId);
      if (!exists) {
        const newConnections = [...connections, { from: connectingFrom.id, to: nodeId, label: '' }];
        setConnections(newConnections);
        pushHistory(nodes, newConnections);
      }
    }
    setConnectingFrom(null);
  }, [connectingFrom, connections, nodes, pushHistory]);

  const handleDeleteConnection = useCallback((from, to) => {
    const newConnections = connections.filter(c => !(c.from === from && c.to === to));
    setConnections(newConnections);
    pushHistory(nodes, newConnections);
  }, [connections, nodes, pushHistory]);

  // Zoom
  const handleZoomIn = () => setZoom(z => Math.min(z + 0.1, 2));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.1, 0.3));
  const handleFit = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Validate
  const handleValidate = useCallback(() => {
    const result = validateFlow(nodes, connections);
    setValidationResult(result);
    return result;
  }, [nodes, connections]);

  // Test / Simulate
  const handleTest = useCallback(() => {
    if (!currentAutomation && nodes.length === 0) {
      setSimulationSteps([{ type: 'error', message: 'No nodes to simulate', timestamp: Date.now() }]);
      return;
    }
    const auto = currentAutomation || { name: 'Untitled Flow', nodes, connections };
    const steps = simulateFlow(auto);
    setSimulationSteps(steps);
  }, [currentAutomation, nodes, connections]);

  // Save
  const handleSave = useCallback(async () => {
    if (nodes.length === 0) return;
    setIsSaving(true);

    const validation = validateFlow(nodes, connections);
    setValidationResult(validation);

    const autoData = {
      name: currentAutomation?.name || 'Untitled Automation',
      description: currentAutomation?.description || '',
      nodes,
      connections,
      status: currentAutomation?.status || 'active',
    };

    try {
      if (currentAutomation) {
        updateAutomation(currentAutomation.id, autoData);
      } else {
        const newAuto = createAutomation(autoData);
        setCurrentAutomation(newAuto);
      }
      setAutomations(loadAutomations());
    } finally {
      setIsSaving(false);
    }
  }, [currentAutomation, nodes, connections]);

  // Load automation
  const handleLoadAutomation = useCallback((auto) => {
    setCurrentAutomation(auto);
    setNodes(JSON.parse(JSON.stringify(auto.nodes || [])));
    setConnections(JSON.parse(JSON.stringify(auto.connections || [])));
    setSelectedNodeIds([]);
    setConnectingFrom(null);
    setSimulationSteps(null);
    setHistory([]);
    setHistoryIndex(-1);
    auto.nodes?.forEach(n => {
      const num = parseInt(n.id.replace(/\D/g, ''));
      if (num >= nodeIdCounter.current) nodeIdCounter.current = num + 1;
    });
  }, []);

  // Delete automation
  const handleDeleteAutomation = useCallback((id) => {
    deleteAutomation(id);
    setAutomations(loadAutomations());
    if (currentAutomation?.id === id) {
      setCurrentAutomation(null);
      setNodes([]);
      setConnections([]);
    }
  }, [currentAutomation]);

  // New automation
  const handleNew = useCallback(() => {
    setCurrentAutomation(null);
    setNodes([]);
    setConnections([]);
    setSelectedNodeIds([]);
    setConnectingFrom(null);
    setSimulationSteps(null);
    setHistory([]);
    setHistoryIndex(-1);
    nodeIdCounter.current = 1;
    setValidationResult({ valid: true, errors: [] });
  }, []);

  // Import template
  const handleImportTemplate = useCallback((imported) => {
    handleLoadAutomation(imported);
    setShowTemplateLibrary(false);
  }, [handleLoadAutomation]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const activeTag = document.activeElement?.tagName;
      const isEditing = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT';

      if ((e.key === 'Delete' || e.key === 'Backspace') && !isEditing) {
        if (selectedNodeIds.length > 0) {
          e.preventDefault();
          handleDeleteNode();
        }
      }
      if (e.key === 'Escape') {
        setSelectedNodeIds([]);
        setConnectingFrom(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        if (!isEditing) {
          e.preventDefault();
          if (e.shiftKey) redo();
          else undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !isEditing) {
        e.preventDefault();
        setSelectedNodeIds(nodes.map(n => n.id));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedNodeIds, handleDeleteNode, undo, redo, nodes]);

  const selectedNode = selectedNodeIds.length === 1 ? nodes.find(n => n.id === selectedNodeIds[0]) : null;

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] min-h-[500px] border border-zinc-800 bg-[#09090B]">
      {/* Saved automations bar */}
      <SavedAutomationsList
        automations={automations}
        onLoad={handleLoadAutomation}
        onDelete={handleDeleteAutomation}
        onNew={handleNew}
      />

      {/* Toolbar */}
      <Toolbar
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFit={handleFit}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        onUndo={undo}
        onRedo={redo}
        onSave={handleSave}
        onTest={handleTest}
        onValidate={handleValidate}
        isValid={validationResult.valid}
        validationErrors={validationResult.errors}
        isSaving={isSaving}
        gridSnap={gridSnap}
        onToggleGridSnap={() => setGridSnap(!gridSnap)}
        onOpenTemplates={() => setShowTemplateLibrary(true)}
      />

      {/* Main editor area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Left: Node panel */}
        <NodePanel
          onAddNode={handleAddNode}
          collapsed={nodePanelCollapsed}
          onToggle={() => setNodePanelCollapsed(!nodePanelCollapsed)}
        />

        {/* Center: Canvas */}
        <FlowCanvas
          nodes={nodes}
          connections={connections}
          selectedNodeIds={selectedNodeIds}
          connectingFrom={connectingFrom}
          zoom={zoom}
          pan={pan}
          gridSnap={gridSnap}
          onSelectNode={handleSelectNode}
          onMoveNode={handleMoveNode}
          onStartConnection={handleStartConnection}
          onEndConnection={handleEndConnection}
          onDeleteConnection={handleDeleteConnection}
          onCanvasClick={handleAddNode}
          onPan={setPan}
          onMultiSelect={handleMultiSelect}
          copiedNodes={copiedNodes}
          onCopy={handleCopy}
          onPaste={handlePaste}
          canvasRef={canvasRef}
        />

        {/* Right: Config panel */}
        <ConfigPanel
          node={selectedNode}
          onUpdate={handleUpdateNode}
          onClose={() => setSelectedNodeIds([])}
        />

        {/* Simulation overlay */}
        {simulationSteps && (
          <SimulationPanel
            steps={simulationSteps}
            onClose={() => setSimulationSteps(null)}
          />
        )}
      </div>

      {/* Template Library Modal */}
      {showTemplateLibrary && (
        <TemplateLibrary
          onImport={handleImportTemplate}
          onClose={() => setShowTemplateLibrary(false)}
        />
      )}
    </div>
  );
});